import axios from 'axios';
import type {
  DrawingSummary, DrawingDetail, Feature, AnalyzeResult, FeatureStatus,
  PageBalloons, Measurement, CapabilityResult, RecognitionJob, UploadJobResponse,
  RegionRect, RegionRecognizeResult,
  FolderNode, FolderTree, FolderDeleteResult, MoveDrawingResult,
  MeasureReportHeader, MeasureReportPreflight, MeasureReportTemplateInfo,
  PartMasterRow, PartMasterUpsert, PartMasterMatch, PartMasterDeleteResult,
  PartMasterBulkFolderResult, PartMasterScope,
  BomEdge, BomEdgeCreate, BomEdgePatch, BomEdgeDeleteResult,
  RecognizedTextList,
  SipPageData, SipDraftListResult, SipDraftSavedResult,
  SipDraftDeleteResult, SipDraftActionResult, SipRenameResult,
  SipTreeResult, SipTreeLeaf, SipTreeNode, SipTreeCustomer,
  TrashList, TrashDetail, TrashRestoreResult, TrashBatchRestoreResult,
  TrashPurgeResult,
  OptionItem, OptionsResponse, OptionDeleteResult, OptionResetResult,
} from '../types';
import type { BalloonStyle } from '../lib/balloonStyle';
import { styleQueryParams } from '../lib/balloonStyle';
import { useAuthStore, type AuthUser } from '../store/auth';

// Vite proxy 将 /api 转发到 127.0.0.1:8771（见 vite.config.ts）
//
// v0.47：凭证走 **HttpOnly Cookie**，不是这里手动加的 Authorization 头。
// ★ 必须如此：画布底图是 `<img src="/api/drawing/1/page/0/png">`，
//   浏览器发 img 请求时不会带任何自定义头 —— 只认头的话登录后图纸是裂图。
//   同源请求浏览器自动带 Cookie，所以这里**什么都不用做**（也不需要
//   `withCredentials`：那是跨源才需要的，而跨源带了也会被后端 CORS 拒）。
export const api = axios.create({
  baseURL: '/api',
  timeout: 60_000,
});

// 把后端返回的 detail 提到 error.message 上。
//
// 为什么必须做：axios 默认只给 "Request failed with status code 404"，用户看到
// 这句等于什么都没看到；而后端每个 4xx/5xx 都写了具体原因（「没有可撤销的删除」
// 「图纸不存在」…）。不提取就只能一边写中文错误、一边在界面上显示英文状态码。
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string' && detail) {
      err.message = detail;
    } else if (Array.isArray(detail)) {
      // FastAPI 参数校验失败：detail 是 [{loc, msg}, ...]
      err.message = detail.map((d: any) => d?.msg || JSON.stringify(d)).join('；');
    }
    // v0.47：会话失效（过期 / 被停用 / 改过密码）⇒ 清掉本地身份，
    // 界面自动切回登录页。**只看 401**：403 是"权限不够"，
    // 那个人还是登录着的，把他踢去登录页只会让人以为是自己掉线了。
    if (err?.response?.status === 401) {
      useAuthStore.getState().markLoggedOut();
    }
    return Promise.reject(err);
  },
);

// ================================================================ 账号与登录（v0.47）
export interface MeResponse {
  auth_enabled: boolean;
  user: AuthUser | null;
  roles: Record<string, string>;
}

/** 启动第一问：要不要登录、现在是谁。**公开接口**（登录页自己也要能调）。 */
export async function fetchMe(): Promise<MeResponse> {
  const r = await api.get('/auth/me');
  return r.data;
}

export async function login(username: string,
                            password: string): Promise<{ user: AuthUser }> {
  const r = await api.post('/auth/login', { username, password });
  return r.data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}

export async function changeMyPassword(oldPassword: string,
                                       newPassword: string): Promise<{ user: AuthUser }> {
  const r = await api.post('/auth/password',
                           { old_password: oldPassword, new_password: newPassword });
  return r.data;
}

export async function listUsers(): Promise<AuthUser[]> {
  const r = await api.get('/users');
  return r.data;
}

export async function createUser(body: {
  username: string; password: string; role: string; display_name?: string | null;
}): Promise<AuthUser> {
  const r = await api.post('/users', body);
  return r.data;
}

/** 改角色 / 显示名 / 启停。**只提交出现的键**（后端用"键在不在"区分
 *  「不动它」与「清空它」，多提交一个 `display_name: undefined` 是安全的，
 *  但把 `role` 一起带上就会覆盖 —— 所以这里手动挑键）。 */
export async function updateUser(
  id: number,
  patch: { role?: string; display_name?: string | null; is_active?: boolean },
): Promise<AuthUser> {
  const body: Record<string, unknown> = {};
  if (patch.role !== undefined) body.role = patch.role;
  if (patch.display_name !== undefined) body.display_name = patch.display_name;
  if (patch.is_active !== undefined) body.is_active = patch.is_active;
  const r = await api.patch(`/users/${id}`, body);
  return r.data;
}

export async function deleteUser(id: number): Promise<{ ok: boolean; id: number }> {
  const r = await api.delete(`/users/${id}`);
  return r.data;
}

export async function resetUserPassword(id: number, newPassword: string,
                                        mustChange = true): Promise<AuthUser> {
  const r = await api.post(`/users/${id}/password`,
                           { new_password: newPassword, must_change: mustChange });
  return r.data;
}

export async function forceLogoutAll(id: number): Promise<{ ok: boolean; revoked: number }> {
  const r = await api.post(`/users/${id}/logout-all`);
  return r.data;
}

export async function health(): Promise<any> {
  const r = await api.get('/health');
  return r.data;
}

export async function listDrawings(): Promise<DrawingSummary[]> {
  const r = await api.get('/drawings');
  return r.data;
}

export async function getDrawing(id: number): Promise<DrawingDetail> {
  const r = await api.get(`/drawing/${id}`);
  return r.data;
}

export async function deleteDrawing(id: number): Promise<void> {
  await api.delete(`/drawing/${id}`);
}

export async function uploadAnalyze(file: File, page = 0,
                                    numbering = 'reading'): Promise<AnalyzeResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('page', String(page));
  form.append('numbering', numbering);
  // V0.5.1：栅格大图（TIF/Image-only PDF）全图 Tile OCR 常需数十秒到数分钟，
  // 远超通用 60s。上传/分析单独用长超时，避免大图中途被 abort 卡在"解析中"。
  const r = await api.post('/drawings/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 10 * 60_000, // 10 分钟
  });
  return r.data;
}

// ================================================================ 统一识别任务（V0.6）
/** 上传 → 立即返回任务受理（drawing_id + 每页 Job），后台识别。前端轮询进度。 */
export async function uploadAsync(file: File, numbering = 'reading',
                                  useOcr = false, lowRes = false,
                                  materialCode = ''): Promise<UploadJobResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('numbering', numbering);
  form.append('use_ocr', String(useOcr));
  form.append('low_res', String(lowRes));
  // v0.85：**归户前置** —— 后端要求这个必填（空值 400）。
  //   物料在分析之前就定下来，识别完不用再回头找归户。
  form.append('material_code', materialCode);
  // v0.78：auto = 允许 OCR 但由页面自己判（矢量文本不足 / 只剩加密水印时才跑）。
  // 显式勾选 use_ocr 时升级为 force。
  form.append('ocr_mode', useOcr ? 'force' : 'auto');
  const r = await api.post('/drawings/upload-async', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60_000,
  });
  return r.data;
}

export async function getJob(jobId: number): Promise<RecognitionJob> {
  const r = await api.get(`/job/${jobId}`);
  return r.data;
}

export async function getDrawingJobs(drawingId: number): Promise<RecognitionJob[]> {
  const r = await api.get(`/drawing/${drawingId}/jobs`);
  return r.data;
}

export async function retryJob(jobId: number, lowRes = false): Promise<RecognitionJob> {
  const r = await api.post(`/job/${jobId}/retry`, null, { params: { low_res: lowRes } });
  return r.data;
}

export async function cancelJob(jobId: number): Promise<RecognitionJob> {
  const r = await api.post(`/job/${jobId}/cancel`);
  return r.data;
}


export async function reanalyze(drawingId: number,
                                options: Record<string, any>): Promise<AnalyzeResult> {
  const r = await api.post(`/drawing/${drawingId}/reanalyze`,
    { drawing_id: drawingId, options },
    { timeout: 10 * 60_000 }); // 栅格 OCR 大图可能跑数分钟
  return r.data;
}

/** 框选区域识别（品质大师式）：只识别框选范围内的尺寸公差，**追加**新气泡。
 *  与「自动识别」不同：不会清空重建既有特征（已确认/已录实测/已拖位置全部保留）。
 *  rect 坐标为页面坐标系（PDF=pt，栅格图=原始像素），与 Feature.x/y 同源。 */
export async function recognizeRegion(
  drawingId: number, pageNo: number, rect: RegionRect,
  options: Record<string, any> = {},
): Promise<RegionRecognizeResult> {
  const r = await api.post(`/drawing/${drawingId}/recognize-region`, {
    page_no: pageNo,
    x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1,
    options,
  }, { timeout: 5 * 60_000 });   // 选区 OCR 通常数秒，大选区留足余量
  return r.data;
}

export async function patchFeature(drawingId: number, featurePk: number,
                                   patch: Record<string, any>): Promise<Feature> {
  const r = await api.patch(`/drawing/${drawingId}/feature/${featurePk}`, patch);
  return r.data;
}

export async function setFeatureStatus(drawingId: number, featurePk: number,
                                       status: FeatureStatus): Promise<Feature> {
  const r = await api.patch(
    `/drawing/${drawingId}/feature/${featurePk}/status?status=${status}`);
  return r.data;
}

/** 真正删除一个特征（后端会级联删 GD&T/气球，并重排 balloon_no + feature_id）。 */
export async function deleteFeature(drawingId: number, featurePk: number): Promise<Feature> {
  const r = await api.delete(`/drawing/${drawingId}/feature/${featurePk}`);
  return r.data;
}

export interface UndoResult {
  restored: Array<{ id: number; feature_id: string; balloon_no: number }>;
  failed: Array<{ feature_id?: string; error: string }>;
  /** 还能继续撤销几次（后端撤销栈深度） */
  remaining: number;
}

/** 撤销该图纸最近一次特征删除（单个或批量）。后进先出，可连续调用。 */
export async function undoDeleteFeature(drawingId: number): Promise<UndoResult> {
  const r = await api.post(`/drawing/${drawingId}/feature/undo`);
  return r.data;
}

/** 还能撤销几次（决定「撤销」入口是否可用）。 */
export async function getUndoDepth(drawingId: number): Promise<{ depth: number }> {
  const r = await api.get(`/drawing/${drawingId}/undo-state`);
  return r.data;
}

// ---------------------------------------------------------------- 多选批量操作
export interface BatchDeleteResult {
  deleted: number;
  deleted_items: Array<{ id: number; feature_id: string; balloon_no: number }>;
  missing: number[];
  remaining: number;
}

export interface BatchStatusResult {
  updated: number;
  updated_items: Array<{ id: number; feature_id: string; balloon_no: number }>;
  missing: number[];
  status: FeatureStatus;
}

/** 「合并为一条」的结果。
 *  merged = 参与合并的条数（含保留下来的那条），removed = 被合并掉的 N-1 条。
 *  ⚠️ 保留条目**沿用原主键**，不会新建特征 —— 因此编号只减不增。 */
export interface MergeResult {
  merged: number;
  kept: { id: number; feature_id: string; balloon_no: number };
  removed: Array<{ id: number; feature_id: string; balloon_no: number }>;
  missing: number[];
  corrected_text: string;
  event_recorded: boolean;
}

/** 多选一键切换状态（确认 / 排除等）。一次事务提交，不改变任何编号。 */
export async function batchSetFeatureStatus(
  drawingId: number, featurePks: number[], status: FeatureStatus,
): Promise<BatchStatusResult> {
  const r = await api.post(`/drawing/${drawingId}/features/batch-status`,
    { feature_pks: featurePks, status });
  return r.data;
}

/** 多选一键删除特征。后端一次事务删除，末尾只重排一次编号
 *  —— balloon_no 与 feature_id 一起自动补位（连续 1..N / F001..F00N）。 */
export async function batchDeleteFeatures(
  drawingId: number, featurePks: number[],
): Promise<BatchDeleteResult> {
  const r = await api.post(`/drawing/${drawingId}/features/batch-delete`,
    { feature_pks: featurePks }, { timeout: 120_000 });
  return r.data;
}

/** 把被错误切分的多条特征合并为一条（v0.37「P2 形态 B」的正样本来源）。
 *
 *  保留阅读顺序最靠前的一条（合并文本写到它身上），其余按删除处理（可撤销）。
 *  `text` 传 undefined 则由后端按阅读顺序拼接各段原文。 */
export async function mergeFeatures(
  drawingId: number, featurePks: number[], text?: string, actor?: string,
): Promise<MergeResult> {
  const r = await api.post(`/drawing/${drawingId}/features/merge`,
    { feature_pks: featurePks, text, actor }, { timeout: 120_000 });
  return r.data;
}

/** 手动添加气泡（v1 能力回归）：在图纸指定 PDF 坐标创建一个特征。 */
export async function createFeature(
  drawingId: number,
  payload: { x: number; y: number; page_no: number;
             characteristic?: string; nominal?: number; tolerance_text?: string;
             unit?: string; is_diameter?: boolean; note?: string },
): Promise<Feature> {
  const r = await api.post(`/drawing/${drawingId}/feature`, payload);
  return r.data;
}

// ================================================================ 气球（v0.21）
/** 保存拖动后的气球位置（center/anchor/leader）。不改变特征状态。 */
export async function updateBalloonPos(
  drawingId: number, featurePk: number,
  patch: { center_x: number; center_y: number; anchor_x?: number;
           anchor_y?: number; leader_points?: number[][] },
): Promise<Feature> {
  const r = await api.put(`/drawing/${drawingId}/balloon/${featurePk}`, patch);
  return r.data;
}

// ================================================================ 报告导出
/** 把后端附件响应保存为本地文件。返回文件名。
 *
 *  v0.31：`kind` 由 5 个收窄到 2 个 —— `'internal'` / `'as9102'` / `'sip-workbook'`
 *  三个导出功能已下线（见下方 v0.31 说明），保留的两个是仍在产的：
 *    · `'balloon-pdf'` → GET /export/balloon-pdf（工具条「气泡 PDF」）
 *    · `'capability'`  → GET /export/capability （AS9102 Form 4 过程能力 Excel）
 */
async function downloadReport(drawingId: number,
                              kind: 'balloon-pdf' | 'capability',
                              params: Record<string, any> = {}): Promise<string> {
  const r = await api.get(`/drawing/${drawingId}/export/${kind}`, {
    params, responseType: 'blob',
  });
  // 文件名优先用后端 Content-Disposition（后端按「源文件名_气泡图_日期_时间」命名）。
  // ⚠️ Starlette 用**小写** `utf-8''` 前缀做 RFC 5987 编码，旧正则只认大写 `UTF-8''`
  //    → 会把 `utf-8''` 当成文件名的一部分，下载后落成 `utf-8''xxx.pdf`（实测踩过）。
  //    故匹配必须**忽略大小写**，并剥掉可能的分号参数。
  let filename = `${kind}_d${drawingId}${kind === 'balloon-pdf' ? '.pdf' : '.xlsx'}`;
  const cd = (r.headers['content-disposition'] as string || '');
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (m) {
    const raw = m[1].trim();
    try { filename = decodeURIComponent(raw); } catch { filename = raw; }
  }
  const blob = new Blob([r.data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 200);
  return filename;
}

// v0.31（2026-10-03，用户要求）：以下四个功能已整体下线（UI + 面板 + 接口）——
// 内部检验报告 / AS9102 FAI / 作业指导书 SIP（含模板编辑器）/ AI 标题栏。
// 对应封装 exportInternal / exportAs9102 / getSipPreflight / exportSipWorkbook /
// listSipTemplates* / getAiStatus / extractTitleblock / getAiAudit 一并移除。
// 保留：exportBalloonPdf（气泡 PDF）。

/**
 * 导出带气泡的图纸 PDF。**Sprint 6.2 起样式所见即所得**：把画布样式面板的
 * 引线/描点开关、颜色、粗细、字号一并传给后端，导出图与画布渲染完全一致
 * （此前只有 scale 生效，关掉引线导出仍有引线 —— 用户报障）。
 */
export const exportBalloonPdf = (drawingId: number, page = 0, withPng = false,
                                 style?: BalloonStyle) =>
  downloadReport(drawingId, 'balloon-pdf', {
    page, png: withPng,
    // v0.77：六个字段的唯一映射搬到 `lib/balloonStyle.ts`（原来三处各抄一遍）
    ...(style ? styleQueryParams(style)
              : { scale: 1, leader: true, anchor: false, color: '',
                  line_width: 0, font_scale: 1 }),
  });

// ================================================================ 画布气球（Sprint 6）
/** 拉取某页特征去重叠后的气球位置（SVG 画布消费，与 PDF 导出一致）。
 *  Sprint 6.1 半径同源：不再传 radius —— 首次布局由后端按页面尺寸自动定，
 *  之后返回落库半径（画布渲染与 PDF 导出同源，所见即所得）。 */
export async function getPageBalloons(drawingId: number,
                                      pageIndex: number): Promise<PageBalloons> {
  const r = await api.get(`/drawing/${drawingId}/page/${pageIndex}/balloons`);
  return r.data;
}

// ================================================================ 实测数据 / 过程能力（Form4 / CPK）
/** 录入/更新单个特性的实测数据（measured 单值或 samples 多样本）。 */
export async function upsertMeasurement(
  drawingId: number, featurePk: number,
  body: { measured?: number | null; samples?: number[]; inspector?: string | null },
): Promise<Measurement> {
  const r = await api.post(`/drawing/${drawingId}/feature/${featurePk}/measurement`, body);
  return r.data;
}

/** 读取单个特性的实测数据（无数据返回全 None/空）。 */
export async function getMeasurement(drawingId: number, featurePk: number): Promise<Measurement | null> {
  const r = await api.get(`/drawing/${drawingId}/feature/${featurePk}/measurement`);
  return r.data;
}

/** 过程能力（CPK/PPK）汇总，只读。 */
export async function getCapability(drawingId: number, threshold = 1.33): Promise<CapabilityResult> {
  const r = await api.get(`/drawing/${drawingId}/capability`, { params: { threshold } });
  return r.data;
}

/** 导出 AS9102 Form4（过程能力）Excel。 */
export const exportCapability = (drawingId: number, meta: Record<string, any> = {}) =>
  downloadReport(drawingId, 'capability', meta);

/** 清空某图纸下全部实测样本（重新测量前复位，避免污染生产库）。 */
export async function clearMeasurements(drawingId: number): Promise<{ deleted: number }> {
  const r = await api.delete(`/drawing/${drawingId}/measurements`);
  return r.data;
}

/** 整张表批量录入实测样本：粘贴多行（首列特性号/气球号，其余为该特性样本），一次性提交。 */
export async function batchMeasurements(
  drawingId: number,
  items: Array<{ feature_id: string; measured?: number | null; samples?: number[]; inspector?: string | null }>,
): Promise<{ applied: number; total: number }> {
  const r = await api.post(`/drawing/${drawingId}/measurements`, { items });
  return r.data;
}

// v0.31（2026-10-03）：SIP《尺寸检验指导书》模板编辑器已整体下线 ——
// 前端入口 + 面板组件 + `/api/sip/templates*`、`/sip-preflight`、`/export/sip-workbook`
// 后端接口一并移除（`encName` 辅助函数同批删除）。

// ================================================================ v0.32 图纸文件夹
// 需求：「图纸区域需要能右键新建文件夹，文件夹里面还能建文件夹」
// 一次性取整棵树（含根目录图纸）而不是逐级懒加载 —— 图纸数量级是「几十张」，
// 折叠展开是纯本地操作，不该每点一次就打一次接口。
export async function getFolderTree(): Promise<FolderTree> {
  const r = await api.get('/folders');
  return r.data;
}

export async function createFolder(name: string, parentId: number | null = null): Promise<FolderNode> {
  const r = await api.post('/folders', { name, parent_id: parentId });
  return r.data;
}

/** 重命名 / 换父级。`fields` 里没出现的键 = 不动那个属性。 */
export async function patchFolder(
  folderId: number,
  fields: { name?: string; parent_id?: number | null },
): Promise<FolderNode> {
  const r = await api.patch(`/folders/${folderId}`, fields);
  return r.data;
}

/** 删除文件夹（含子孙）。**其中的图纸退回根目录，不会被删**。 */
export async function deleteFolder(folderId: number): Promise<FolderDeleteResult> {
  const r = await api.delete(`/folders/${folderId}`);
  return r.data;
}

/** 把图纸移到某文件夹；`folderId=null` = 移回根目录。
 *
 *  ★ v0.57：**图纸的文件夹跟随它的归属零件** —— 有归属零件时，零件与该零件
 *    名下**全部**图纸一起搬（返回值里 `moved_parts` / `moved_drawings` 就是
 *    给提示报数用的）；未归户的图纸只动它自己。
 *  ⚠️ 返回值从 `DrawingSummary` 换成 `MoveDrawingResult`：包装一层是为了
 *    带上那两个数，否则界面只能写死「已移动」。
 */
export async function moveDrawingToFolder(
  drawingId: number, folderId: number | null,
): Promise<MoveDrawingResult> {
  const r = await api.patch(`/drawing/${drawingId}/folder`, { folder_id: folderId });
  return r.data;
}

// ================================================================ v0.32 测量报告
// 需求：「读取测量报告模板，将图纸信息、标准值、公差、测量工具等信息导入到测量报告模板里面」
export async function getMeasureTemplate(): Promise<MeasureReportTemplateInfo> {
  const r = await api.get('/measure-report/template');
  return r.data;
}

export async function uploadMeasureTemplate(file: File): Promise<MeasureReportTemplateInfo> {
  const form = new FormData();
  form.append('file', file);
  const r = await api.post('/measure-report/template', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  });
  return r.data;
}

/** 恢复为内置模板（已上传的模板文件留在磁盘上，不删）。 */
export async function resetMeasureTemplate(): Promise<MeasureReportTemplateInfo> {
  const r = await api.delete('/measure-report/template');
  return r.data;
}

/** 导出前预检：只读，不改数据。 */
export async function measureReportPreflight(
  drawingId: number, page = 0,
): Promise<MeasureReportPreflight> {
  const r = await api.get(`/drawing/${drawingId}/measure-report/preflight`, {
    params: { page }, timeout: 120_000,
  });
  return r.data;
}

/**
 * 生成并下载《测量报告》.xlsx。
 *
 * 表头走 query（与「气泡 PDF」同一套 blob 下载机制）：12 个短字符串放 query
 * 完全够用，比 POST body 后再造 objectURL 命名更稳（Chrome 对 POST 下载容易拦）。
 * ⚠️ 传空串会被后端当「没填」丢掉，所以这里先剔掉空值再发。
 */
export async function exportMeasureReport(
  drawingId: number,
  header: MeasureReportHeader,
  opts: { page?: number; balloon?: boolean } = {},
): Promise<string> {
  const params: Record<string, any> = {
    page: opts.page ?? 0,
    balloon: opts.balloon ?? true,
  };
  for (const [k, v] of Object.entries(header)) {
    const s = (v ?? '').toString().trim();
    if (s) params[k] = s;
  }
  // 单独走一次（downloadReport 的 kind 白名单不含测量报告），但文件名解析同一套
  const r = await api.get(`/drawing/${drawingId}/export/measure-report`, {
    params, responseType: 'blob', timeout: 10 * 60_000,
  });
  let filename = `测量报告_d${drawingId}.xlsx`;
  const cd = (r.headers['content-disposition'] as string || '');
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (m) {
    const raw = m[1].trim();
    try { filename = decodeURIComponent(raw); } catch { filename = raw; }
  }
  const blob = new Blob([r.data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
  return filename;
}

// ================================================================ 零件主数据（v0.38）
/** 列表（`q` 为模糊筛选：图号 / 名称 / 客户 / 模具号 / 材质 …）。
 *
 *  `scope`（v0.43）是**归属范围**：全部 / 某个文件夹 / 未分类。
 *  ⚠️ 三个取值互斥，别写成两个散参数（`folderId?: number|null` + `unfiled?: boolean`）——
 *     `null` 会同时表示"不筛选"和"未分类"两种意思，正是兜底 bug 的温床。
 *     后端那边 `folder_id=0` 是**合法值**（返回空列表，不是"不过滤"）。
 */
/** 台账列表的底层实现（v0.48）—— **一次请求同时拿两样东西**：
 *    · `rows` —— 零件行；
 *    · `hiddenChildren` —— 被折叠的子零件条数（响应头 `X-Hidden-Children`）。
 *
 *  ★ `topOnly=true` 时后端只回**顶层零件**（v0.48 口径：子零件不单独占行，
 *    改在主零件的展开区里按列显示）。
 *  ⚠️ 「另有 N 条」走的是**响应头**而不是返回体：返回体是
 *    `list[PartMasterRow]`（v0.38 起的契约），改成对象会让**所有**调用方一起红。
 *    同源请求本来就能读自定义头；`main.py` 的 CORS 也开了 `expose_headers`。
 *  ⚠️ 读不到头时**不报错、按 0 处理** —— 提示条少一个数字，好过整页打不开。
 *    但「恒为 0 的诊断」必须是**活的**：后端每条 /part-master 响应都带这个头
 *    （不 top_only 时也回 "0"），`backend/tests/test_part_master_v048.py` 里有断言。
 */
async function _fetchPartMaster(q?: string,
                                scope: PartMasterScope = { kind: 'all' },
                                topOnly = false)
  : Promise<{ rows: PartMasterRow[]; hiddenChildren: number }> {
  const params: Record<string, any> = {};
  if (q) params.q = q;
  // v0.85：左栏维度从「文件夹」改成「客户」。
  // ⚠️ folder/unfiled 两个分支**保留**（`DrawingPartChip` 之类老调用还在传），
  //    但 BOM 数据源下后端会忽略它们 —— 与 v0.82 的处置一致。
  if (scope.kind === 'customer') params.customer = scope.name;
  else if (scope.kind === 'folder') params.folder_id = scope.id;
  else if (scope.kind === 'unfiled') params.unfiled = true;
  if (topOnly) params.top_only = true;
  const r = await api.get('/part-master', { params });
  const n = Number.parseInt(String(r.headers?.['x-hidden-children'] ?? ''), 10);
  return { rows: r.data, hiddenChildren: Number.isFinite(n) ? n : 0 };
}

/** 台账列表 —— **全部零件**（含子零件，v0.48 前的口径）。
 *
 *  用途：编辑面板的「+ 添加子零件」候选列表。那里**必须**看得到子零件 ——
 *  一个零件要挂到第二个总成下时，它自己是别人的子件，藏起来就没法选了。
 */
export async function listPartMaster(q?: string,
                                     scope: PartMasterScope = { kind: 'all' })
  : Promise<PartMasterRow[]> {
  return (await _fetchPartMaster(q, scope, false)).rows;
}

/** 台账列表页用（v0.48）—— 带回「折叠了几个子零件」给提示条报数。
 *
 *  ⚠️ 与 `listPartMaster` 是**同一份实现**（都走 `_fetchPartMaster`）：
 *    两处各拼一遍 params，早晚会出现"一个传 top_only、一个忘传"，
 *    而现象是「列表和候选列表数目对不上」—— 很难归因到参数上。
 */
export async function listPartMasterPage(q?: string,
                                         scope: PartMasterScope = { kind: 'all' },
                                         topOnly = true)
  : Promise<{ rows: PartMasterRow[]; hiddenChildren: number }> {
  return _fetchPartMaster(q, scope, topOnly);
}

/** v0.85（沈哥 2026-10-22）：**客户维度**统计 —— 左栏数据源。
 *
 *  只统计 P/B/D 开头的物料编码（与列表接口同一口径）。
 *  `customer === '__none__'` 的那一项在界面上显示成「其他」（客户字段为空），
 *  后端已把它**排在最后**，前端不要重排（否则它会混进真客户中间）。 */
export async function fetchPartCustomers(): Promise<{
  total: number;
  customers: Array<{ customer: string; label: string; count: number }>;
}> {
  const r = await api.get('/part-master/customers');
  return r.data;
}

/** 把**一个**零件归到某文件夹（`folderId=null` = 退回未分类）。v0.43
 *
 *  ★ 归属不走 `updatePartMaster`：后端 PUT 是**全量**语义，把 `folder_id` 混进去
 *    意味着前端任何一次漏提交都会把分类**静默清空**且不报错。
 *  ⚠️ 目标文件夹不存在 ⇒ 后端 404（不会静默改成未分类）。
 */
export async function setPartFolder(pmId: number,
                                    folderId: number | null): Promise<PartMasterRow> {
  const r = await api.patch(`/part-master/${pmId}/folder`, { folder_id: folderId });
  return r.data;
}

/** 把选中的零件**批量**归到同一个文件夹（`folderId=null` = 批量退回未分类）。v0.43
 *
 *  ★ v0.57：这批零件**名下的图纸跟着一起搬**（`moved_drawings` 是实际跟随的张数）。
 *    归属不变式：有归属零件的图纸，其 `folder_id` 必须等于那个零件的 `folder_id`。
 */
export async function bulkSetPartFolder(ids: number[],
                                        folderId: number | null)
  : Promise<PartMasterBulkFolderResult> {
  const r = await api.post('/part-master/bulk-folder',
                           { ids, folder_id: folderId });
  return r.data;
}

export async function getPartMaster(id: number): Promise<PartMasterRow> {
  const r = await api.get(`/part-master/${id}`);
  return r.data;
}

export async function createPartMaster(body: PartMasterUpsert): Promise<PartMasterRow> {
  const r = await api.post('/part-master', body);
  return r.data;
}

/** 全量更新 16 项。**未提供的键会被置空**（PUT 语义），必须整包提交。 */
export async function updatePartMaster(id: number,
                                       body: PartMasterUpsert): Promise<PartMasterRow> {
  const r = await api.put(`/part-master/${id}`, body);
  return r.data;
}

export async function deletePartMaster(id: number): Promise<PartMasterDeleteResult> {
  const r = await api.delete(`/part-master/${id}`);
  return r.data;
}

export async function bulkDeletePartMaster(ids: number[]): Promise<PartMasterDeleteResult> {
  const r = await api.post('/part-master/bulk-delete', { ids });
  return r.data;
}

/** 导出 CSV（走 blob 下载：与「测量报告」同一套机制，中文名从
 *  `content-disposition` 解析）。`ids` 为空 = 导出全部。 */
export async function exportPartMaster(ids?: number[]): Promise<string> {
  const params: Record<string, any> = {};
  if (ids && ids.length) params.ids = ids.join(',');
  const r = await api.get('/part-master/export', {
    params, responseType: 'blob', timeout: 120_000,
  });
  let filename = 'part_master.csv';
  const cd = (r.headers['content-disposition'] as string || '');
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (m) {
    const raw = m[1].trim();
    try { filename = decodeURIComponent(raw); } catch { filename = raw; }
  }
  const url = URL.createObjectURL(new Blob([r.data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
  return filename;
}

// ================================================================ v0.64 批量导出
/** 可勾选的内容项。**清单的唯一出处在后端**（`batch_export_service.CONTENTS`），
 *  前端只渲染它 —— 以后加一道工序不用改两处。 */
export interface BatchExportOption {
  key: string;
  label: string;
  default: boolean;
}

export interface BatchExportOptions {
  contents: BatchExportOption[];
  sip_kinds: string[];
}

export interface BatchExportPart {
  id: number;
  folder: string;
  part_no: string;
  part_name: string;
  drawings: { id: number; filename: string; page_count: number; stem: string }[];
}

export interface BatchExportPlan {
  parts: BatchExportPart[];
  part_count: number;
  drawing_count: number;
  no_drawing: string[];
}

export interface BatchExportResult {
  filename: string;
  ok: number;
  skipped: number;
  failed: number;
  elapsed: number;
}

export async function fetchBatchExportOptions(): Promise<BatchExportOptions> {
  const r = await api.get('/batch-export/options');
  return r.data;
}

/** 导出前的一眼：这些零件名下有哪些图纸、哪个零件还没归户图纸。只读。 */
export async function previewBatchExport(parts: number[]): Promise<BatchExportPlan> {
  const r = await api.get('/batch-export/preview',
                          { params: { parts: parts.join(',') } });
  return r.data;
}

/** 打包下载 zip，返回实际写入/跳过/失败的条数。
 *
 *  ⚠️ 超时给到 5 分钟：全厂 6 个零件全勾实测 ~25 秒（首次还要落气泡布局），
 *    默认 60 秒会在慢机器上把一次成功的打包判成失败。
 *  ⚠️ 出错时 `detail` 藏在 **Blob** 里（`responseType:'blob'` 连错误响应也是
 *    Blob），全局拦截器读不到 `.detail` —— 这里自己解一次，否则界面只会显示
 *    "Request failed with status code 400"。
 *
 *  ★ v0.77：样式是**按图纸**存的 ⇒ 一次打包里不同图纸可能各要一套。
 *    `styleByDrawing` 只带「本图纸单独设过」的那些（`{drawingId: BalloonStyle}`），
 *    其余图纸用 `style` 兜底。全部图纸都塞进来会让 GET 的 URL 随零件数线性变长，
 *    而后端对未列出的图纸本来就要回落 —— 少传 = 少一次超长 URL 的风险。
 */
export async function exportBatch(parts: number[], contents: string[],
                                  skipEmpty = true,
                                  style?: BalloonStyle,
                                  styleByDrawing?: Record<number, BalloonStyle>)
                                  : Promise<BatchExportResult> {
  const params: Record<string, any> = {
    parts: parts.join(','),
    contents: contents.join(','),
    skip_empty: skipEmpty,
  };
  // v0.69：气泡图 / SIP 里的气泡图都按画布样式画（关掉引线就真的没有引线）
  if (style) Object.assign(params, styleQueryParams(style));
  const overrides: Record<string, unknown> = {};
  for (const [id, s] of Object.entries(styleByDrawing || {})) {
    overrides[id] = styleQueryParams(s);
  }
  if (Object.keys(overrides).length) params.styles = JSON.stringify(overrides);
  let r;
  try {
    r = await api.get('/batch-export',
                      { params, responseType: 'blob', timeout: 300_000 });
  } catch (e: any) {
    const d = e?.response?.data;
    if (d instanceof Blob) {
      try {
        const j = JSON.parse(await d.text());
        if (typeof j?.detail === 'string' && j.detail) e.message = j.detail;
      } catch { /* 不是 JSON 就保持原错 */ }
    }
    throw e;
  }
  let filename = '批量导出.zip';
  const cd = (r.headers['content-disposition'] as string || '');
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (m) {
    const raw = m[1].trim();
    try { filename = decodeURIComponent(raw); } catch { filename = raw; }
  }
  const url = URL.createObjectURL(new Blob([r.data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
  return {
    filename,
    ok: Number(r.headers['x-batch-ok'] ?? 0),
    skipped: Number(r.headers['x-batch-skipped'] ?? 0),
    failed: Number(r.headers['x-batch-failed'] ?? 0),
    elapsed: Number(r.headers['x-batch-elapsed'] ?? 0),
  };
}

/** 打开图纸时自动带出：抽取本图字段 → 按 零件编号/内部编号 找台账（**只认唯一命中**）。 */export async function matchPartMaster(drawingId: number,
                                      page = 0): Promise<PartMasterMatch> {
  const r = await api.get(`/drawing/${drawingId}/part-master/match`,
                          { params: { page_index: page } });
  return r.data;
}

/** 用图纸上能抽到的信息新建一行，并把这图纸归户到新行。 */
export async function createPartMasterFromDrawing(
  drawingId: number, page = 0,
): Promise<PartMasterRow> {
  const r = await api.post(`/drawing/${drawingId}/part-master/from-drawing`,
                           null, { params: { page_index: page } });
  return r.data;
}

/** 归户 / 解除归户（`partMasterId=null` = 解除）。 */
export async function bindDrawingPartMaster(
  drawingId: number, partMasterId: number | null,
): Promise<{ drawing_id: number; part_master_id: number | null }> {
  const r = await api.patch(`/drawing/${drawingId}/part-master`,
                            { part_master_id: partMasterId });
  return r.data;
}

/**
 * 该零件**已归户**的图纸清单（零件主数据编辑面板的「关联图纸」一栏）。
 *
 * ★ 只读接口：关联图纸**不可能上传** —— 候选来自 `listDrawings()`
 *   （图纸工作台里已有的图纸），本接口也不产生任何图纸。
 */
export async function listPartMasterDrawings(pmId: number): Promise<DrawingSummary[]> {
  const r = await api.get(`/part-master/${pmId}/drawings`);
  return r.data;
}

// ================================================== BOM 父子关系（v0.41）
// 需求（2026-10-11 用户原话）：「BOM那里需要优化多级，总成、总成下面还有子零件」
//
// ★ 关系存在边表 `bom_edges` 里，**零件表始终一行** —— 用户明确「共用件很常见」
//   （同一颗簧片螺母装在不同总成上）。给零件表加 `parent_id` 做不到这件事：
//   一个零件只能有一个父件，要在第二个总成下出现就得再建一行，而四列唯一键会
//   拒绝那一行。边表没有这个约束。
// ★ 所有 `edgeId` 都是**边**的 id，不是零件 id（两者都是小整数，传错会改到
//   另一个零件的关系且很难看出来）。

/** 该总成下面的子零件（含用量 / 位号）。没有子件时返回**空数组**。 */
/** 该总成下面的子零件（含用量 / 位号）。
 *
 *  `withRow=true`（v0.48）：每条边额外带对面**完整的零件行**（`edge.row`）——
 *  列表页的展开区要按「表头勾选了哪几列就显示哪几列」渲染子零件，
 *  只有 `part_no / part_name / internal_no` 三个摘要字段是不够的。
 *  ⚠️ 默认 `false`：编辑面板的「子零件（BOM）」栏只需要摘要，
 *     多带一份整行是白拉数据。
 */
export async function listPartChildren(pmId: number, withRow = false)
  : Promise<BomEdge[]> {
  const r = await api.get(`/part-master/${pmId}/children`,
                          withRow ? { params: { with_row: true } } : undefined);
  return r.data;
}

/** 该零件装在哪些总成下（含用量 / 位号）—— **共用件会返回多条**。 */
export async function listPartParents(pmId: number): Promise<BomEdge[]> {
  const r = await api.get(`/part-master/${pmId}/parents`);
  return r.data;
}

/** 把一个已存在的零件挂成 `pmId` 的子件。
 *  失败了会有中文提示（自己挂自己 / 已在里面 / 成环 / 不存在）。 */
export async function addPartChild(pmId: number, body: BomEdgeCreate): Promise<BomEdge> {
  const r = await api.post(`/part-master/${pmId}/children`, body);
  return r.data;
}

/** 改这一条关系的用量 / 位号 / 备注（**改不了父子**：换父子 = 删旧建新）。 */
export async function updateBomEdge(edgeId: number,
                                    body: BomEdgePatch): Promise<BomEdge> {
  const r = await api.patch(`/part-master/bom-edge/${edgeId}`, body);
  return r.data;
}

/** 解除父子关系。**两个零件行都保留** —— 删的是关系，不是实物。 */
export async function removeBomEdge(edgeId: number): Promise<BomEdgeDeleteResult> {
  const r = await api.delete(`/part-master/bom-edge/${edgeId}`);
  return r.data;
}

// ================================================== 识别文字（v0.42）
// 需求（2026-10-11 用户原话）：「导入图纸识别到的内容需要在主数据界面体现」
// 口径（用户选定）：「**先把 OCR 原文列给我看**」—— 先摆证据，不自动填 16 项。
//
// ⚠️ 这是**只读**接口：文字是识别时落库的证据，界面上不能改。
//    让界面能改文字，等于把"识别到底读到了什么"变成可编辑的结论，
//    证据就没了。

/** 这张图**本次识别读到的文字原文**。
 *
 *  ★ `res.recorded === false` 时必须给用户一句明确提示：这张图识别于 v0.42
 *    之前，当时**没有记录文字**，`total === 0` 不代表没读到。不提示的话，
 *    用户会以为识别功能坏了（而重新识别一次其实就能看到）。
 */
export async function fetchDrawingTexts(
  drawingId: number,
  opts?: { pageNo?: number; q?: string; source?: 'vector' | 'ocr'; limit?: number },
): Promise<RecognizedTextList> {
  const params: Record<string, unknown> = {};
  // ⚠️ 逐个判 `!= null` 而不是用 `|| undefined` —— `pageNo = 0` 是**合法页码**
  //    （第 1 页），`0 || undefined` 会把它悄悄丢掉，变成"取全部页"。
  if (opts?.pageNo != null) params.page_no = opts.pageNo;
  if (opts?.q) params.q = opts.q;
  if (opts?.source) params.source = opts.source;
  if (opts?.limit != null) params.limit = opts.limit;
  const r = await api.get(`/drawing/${drawingId}/texts`, { params });
  return r.data;
}

// v0.31（2026-10-03）：AI 标题栏字段抽取已整体下线 —— 前端入口 + 面板组件 +
// `/api/ai/status`、`/api/ai/audit`、`/api/drawing/{id}/ai/titleblock` 后端接口
// 一并移除（含 backend/services/ai/ 整个子系统）。


// ================================================== SIP 作业指导书（v0.50）
// 需求（2026-10-14 用户原话）：
//   「不同的制程SIP点进去单独出现编辑页面」   → 五份文档各一个编辑态
//   「零件主数据里面手动输入的信息要跟后面 SIP 联动」 → 表头**只读**，实时从台账取
//
// ★ 五个接口都在 `/api/drawing/{id}/sip...` 下 —— SIP 是**图纸**的产物
//   （明细行 = 这张图的气泡号 + 尺寸 + 公差），所以入口再怎么从零件侧进，
//   最终也要落到一张具体的图纸上（见 `types/index.ts` 的 `SipEntry`）。
//
// ★ 导出走 **GET + blob**，与「气泡 PDF」「测量报告」同一套机制 ——
//   本项目导出全是 GET（权限矩阵里就是 viewer 级，见 `auth_guard`）。
//   「用的是草稿还是默认分配」通过响应头回传，不塞进文件里。

/** 该图纸**每一份** SIP 的草稿状态（给左侧的「已有草稿」角标 + 多份列项）。 */
export async function fetchSipDocs(drawingId: number): Promise<SipDraftListResult> {
  const r = await api.get(`/drawing/${drawingId}/sip`);
  return r.data;
}

/**
 * SIP 选图层级的「物料树」（v0.90，沈哥重设计）。
 *
 * · `q` 为空 ⇒ 返回 客户 → 产品(成品)/半成品 → SIP 三层嵌套（`mode:"tree"`）；
 *   叶子是 SIP（不是图纸），点叶子直接打开那一份。
 * · `q` 非空 ⇒ 返回扁平命中的 SIP 叶子（`mode:"search"`，含 `breadcrumb`），
 *   覆盖未归户 / 原材料类图纸（树里不显示、靠搜索触达）。
 */
export async function fetchSipTree(q = ""): Promise<SipTreeResult> {
  const params: Record<string, string> = {};
  if (q && q.trim()) params.q = q.trim();
  const r = await api.get(`/sip/tree`, { params });
  return r.data;
}

/**
 * 某一份 SIP 的编辑页初始态。
 *
 * ⚠️ 不要在这里 `catch` 掉错误再返回空对象 —— `res.source` 明确区分
 *   `draft` / `default`，界面要靠它提示「这 N 行是按工序规则现算的，请过一眼」。
 *   吞掉异常会把它显示成「0 行」，看起来像这份文档本来就没内容。
 *
 * ★ `seq` 是**第几份**（0 = 第一份）：v0.55 起同一工序可以有多份。
 *   默认 0 ⇒ 不传的老调用行为逐格不变。
 */
export async function fetchSipPage(drawingId: number, docKind: string,
                                   seq = 0, routeOpId = 0): Promise<SipPageData> {
  // ★ v0.83：`route_op_id` 非 0 = 打开某道工序的 SIP（与常规五份各存各的草稿）。
  //   不传就等同于 0 ⇒ 老调用方行为不变。
  const params: Record<string, number> = { seq };
  if (routeOpId) params.route_op_id = routeOpId;
  const r = await api.get(`/drawing/${drawingId}/sip/${docKind}`,
                          { params });
  return r.data;
}

/* ---------------------------------------------------------------- 工序清单（v0.83）
 * 🔴 工序/工艺**只能来自 BOM 平台**，本平台不提供手工维护
 *    （手工新增接口返回 410 Gone，前端连入口都不给）。
 *    所以这里只有「读」和「从 BOM 导入」两类，没有 create/update。
 */

/** 一道工序（`route_ops` 一行）。 */
export interface RouteOp {
  id: number;
  drawing_id: number;
  op_no: number;
  op_name: string;
  op_code?: string | null;
  work_center?: string | null;
  /** 只可能是 `bom`（手工维护已封死） */
  source: string;
  route_code?: string | null;
  material_code?: string | null;
  remark?: string | null;
  /** 这道工序的 SIP 填了没有 / 填了几项 */
  sip_edited?: boolean;
  sip_item_count?: number;
}

/** 该图纸的工序清单（按工序号升序）。 */
export async function fetchRouteOps(drawingId: number): Promise<{
  drawing_id: number; ops: RouteOp[];
}> {
  const r = await api.get(`/drawing/${drawingId}/ops`);
  return r.data;
}

/** 该图纸归户零件在 BOM 里可选的工艺路线（一料多路线 ⇒ 由人选，不自动猜）。 */
export async function fetchBomRoutes(drawingId: number): Promise<{
  drawing_id: number;
  /** `null` = 图纸还没归户（或归户的零件 BOM 里没有） */
  material_code: string | null;
  routes: Array<{
    route_code: string;
    route_name?: string | null;
    op_count: number;
    first_seq?: number | null;
    last_seq?: number | null;
  }>;
}> {
  const r = await api.get(`/drawing/${drawingId}/ops/bom-routes`);
  return r.data;
}

/* ------------------------------------------------------ 图纸归户（v0.83.1）
 * 老接口 `PATCH /drawing/{id}/part-master` 随 `part_master` 一起 410 了，
 * 这一组直接写 `drawings.part_no`（= BOM 物料编码）。
 */

export interface DrawingPartNoInfo {
  drawing_id: number;
  /** 当前归户的物料编码（null = 未归户） */
  part_no: string | null;
  bound: boolean;
  material: {
    material_code: string;
    material_name: string | null;
    specification: string | null;
    material_type: string | null;
    route_op_count: number;
  } | null;
}

/** 这张图纸当前归户到哪个物料。 */
export async function fetchDrawingPartNo(drawingId: number)
  : Promise<DrawingPartNoInfo> {
  const r = await api.get(`/drawing/${drawingId}/part-no`);
  return r.data;
}

/** 归户 / 解除归户（传 null 或空串 = 解除）。 */
export async function setDrawingPartNo(drawingId: number,
                                     materialCode: string | null)
  : Promise<DrawingPartNoInfo> {
  const r = await api.patch(`/drawing/${drawingId}/part-no`,
                            { material_code: materialCode });
  return r.data;
}

/** 该物料的 **BOM 子件**（v0.87，来自 BOM 平台镜像 `bom_child_ref`）。
 *
 *  ★ 参数是**物料编码**不是行 id：镜像表每次同步会 `TRUNCATE ... RESTART IDENTITY`，
 *    行 id 会重新编号，缓存 id 再请求就可能指到别的物料。编码是业务主键。
 */
export async function fetchBomChildren(materialCode: string): Promise<{
  parent_code: string;
  count: number;
  items: Array<{
    child_code: string;
    child_name: string | null;
    qty: number | null;
    seq_no: number | null;
    category: string | null;
  }>;
}> {
  const r = await api.get(
    `/part-master/by-code/${encodeURIComponent(materialCode)}/bom-children`);
  return r.data;
}

/** 保存**BOM 没提供**的字段（v0.89）。
 *
 *  值给空串 ⇒ 删掉该字段的覆盖。后端会拒绝覆盖 BOM 已有值的字段（`skipped`）。
 */
export async function savePartFields(materialCode: string,
                                    fields: Record<string, string>): Promise<{
  material_code: string; updated: number; cleared: number; skipped: number;
}> {
  const r = await api.put(`/part-field/${encodeURIComponent(materialCode)}`,
                          { fields });
  return r.data;
}

/** 该物料**装在哪些总成下面**（v0.87，反查 `bom_child_ref`）。 */
export async function fetchBomParents(materialCode: string): Promise<{
  child_code: string; count: number;
  items: Array<{ parent_code: string; parent_name: string; qty: number | null;
                 seq_no: number | null }>;
}> {
  const r = await api.get(
    `/part-master/by-code/${encodeURIComponent(materialCode)}/bom-parents`);
  return r.data;
}

/** 归户到该物料的图纸（v0.87，按 `drawings.part_no` 查）。 */
export async function fetchPartDrawings(materialCode: string): Promise<{
  material_code: string; count: number;
  items: Array<{ id: number; filename: string; page_count: number | null;
                 part_no: string | null }>;
}> {
  const r = await api.get(
    `/part-master/by-code/${encodeURIComponent(materialCode)}/drawings`);
  return r.data;
}

/** 维护「零件编号（客户物料编码）」（v0.86）。
 *
 *  ★ 写的是**本地覆盖层** `part_no_overrides`，不是 BOM 镜像表 ——
 *    镜像表每天 03:10 会被 TRUNCATE 重建，写在那里等于白写。
 *  `partNo` 传空串 ⇒ 删掉覆盖，回落到 BOM 镜像值。
 */
export async function setPartNoOverride(materialCode: string, partNo: string): Promise<{
  material_code: string; part_no: string | null; cleared: boolean;
}> {
  const r = await api.put(`/part-no/${encodeURIComponent(materialCode)}`,
                          { part_no: partNo });
  return r.data;
}

/** 搜 BOM 物料（**不绑定图纸**）—— 上传前「先选物料」用（v0.85）。
 *  只返回 P/B/D 开头的物料（与零件主数据列表同一口径）。 */
export async function searchBomMaterials(q = '', limit = 30): Promise<{
  q: string;
  items: Array<{
    material_code: string;
    material_name: string | null;
    specification: string | null;
    material_type: string | null;
    customer: string;
  }>;
}> {
  const r = await api.get('/bom-materials', { params: { q, limit } });
  return r.data;
}

/** 搜 BOM 物料（归户面板的下拉数据源）。 */
export async function fetchBomCandidates(drawingId: number, q = ''): Promise<{
  q: string;
  items: Array<{
    material_code: string;
    material_name: string | null;
    specification: string | null;
    material_type: string | null;
  }>;
}> {
  const r = await api.get(`/drawing/${drawingId}/part-no/candidates`,
                          { params: { q } });
  return r.data;
}

/* ---------------------------------------------------------------- 检验任务（v0.84）
 * ⚠️ 这些是**内部接口**（登录会话鉴权）。对外 ASN/MES 的 `/api/external/v1/...`
 *    是 P5 的事，走 `X-API-Key`，不在前端调用范围。
 */

export interface InspectionTaskRow {
  id: number;
  task_no: string;
  source: string;
  task_type: string;
  status: string;
  result: string | null;
  drawing_id: number | null;
  material_code: string | null;
  part_no: string | null;
  route_op_id: number | null;
  lot_no: string | null;
  qty: number | null;
  sample_qty: number | null;
  /** 断点游标（第一维：第几项，0 起） */
  current_seq: number;
  /** 断点游标（第二维：第几件，0 起）—— v0.85 双模式必需 */
  current_sample?: number;
  /** 抽样件数（= `sample_qty`，最少 1；建单时定死，执行中不能改） */
  sample_count?: number;
  external_ref: string | null;
  created_at: string | null;
  total: number;
  done: number;
  failed: number;
  progress: number;
  items?: Array<Record<string, any>>;
}

/** 任务列表（新→旧）。 */
export async function fetchInspectionTasks(params: {
  status?: string; source?: string; task_type?: string; q?: string;
  limit?: number;
} = {}): Promise<{ total: number; tasks: InspectionTaskRow[] }> {
  const r = await api.get('/inspection-tasks', { params });
  return r.data;
}

/** 单张任务（含明细快照）。 */
export async function fetchInspectionTask(taskNo: string)
  : Promise<InspectionTaskRow> {
  const r = await api.get(`/inspection-tasks/${encodeURIComponent(taskNo)}`);
  return r.data;
}

/** 建任务 —— **明细不在请求里**，由后端从 SIP 快照过来。
 *  取不到明细（工序 SIP 没勾气泡）会回 400 + 明确原因。 */
export async function createInspectionTask(body: {
  task_type: string;
  source?: string;
  drawing_id?: number;
  material_code?: string;
  part_no?: string;
  route_op_id?: number;
  sip_doc_kind?: string;
  sip_seq?: number;
  lot_no?: string;
  qty?: number;
  sample_qty?: number;
  external_ref?: string;
  remark?: string;
}): Promise<InspectionTaskRow & { duplicated?: boolean }> {
  const r = await api.post('/inspection-tasks', body, { timeout: 60_000 });
  return r.data;
}

/** 记录**一格**实测值（某项 × 某件），返回整张任务的最新状态。
 *  ⚠️ 超差（`fail`）而 `note` 为空会 **400** —— 后端拒绝落库。 */
export async function measureTaskItem(taskNo: string, body: {
  seq: number;
  /** 第几个样件（**0 起**，界面显示 +1） */
  sample_no: number;
  value?: number | null;
  note?: string;
  verdict?: string | null;
}): Promise<InspectionTaskRow> {
  const r = await api.post(
    `/inspection-tasks/${encodeURIComponent(taskNo)}/measure`, body);
  return r.data;
}

/** 收尾：还有项没测会被 **409** 拒绝（并指出缺哪几项）。 */
export async function finishInspectionTask(taskNo: string)
  : Promise<InspectionTaskRow> {
  const r = await api.post(
    `/inspection-tasks/${encodeURIComponent(taskNo)}/finish`);
  return r.data;
}

/** 执行界面右侧气泡图的数据（图纸页尺寸 + 全部特征）。 */
export async function fetchTaskCanvas(taskNo: string): Promise<{
  task_no: string;
  drawing_id: number | null;
  filename?: string;
  page_index: number;
  width_pt: number;
  height_pt: number;
  features: Array<Record<string, any>>;
}> {
  const r = await api.get(
    `/inspection-tasks/${encodeURIComponent(taskNo)}/canvas`);
  return r.data;
}

/** 撤销任务（已完成的会被 409 拒绝 —— 受控记录不可改写）。 */
export async function cancelInspectionTask(taskNo: string, reason = '')
  : Promise<{ task_no: string; status: string; changed: boolean }> {
  const r = await api.post(
    `/inspection-tasks/${encodeURIComponent(taskNo)}/cancel`, { reason });
  return r.data;
}

/** 按物料编码 / 客户零件号查「能建任务的图纸 + 工序」。 */
export async function findTaskCandidates(part: string): Promise<{
  part: string;
  material_code?: string;
  part_no?: string;
  candidates: Array<{
    drawing_id: number;
    filename: string;
    part_no: string | null;
    ops: Array<{ id: number; op_no: number; op_name: string;
                 work_center?: string | null; source?: string }>;
  }>;
}> {
  const r = await api.get('/inspection-tasks/candidates', { params: { part } });
  return r.data;
}

/** 从 BOM 导入工序（**全量替换**本图纸原有工序，含挂在旧工序上的 SIP）。 */
export async function importRouteOps(drawingId: number,
                                     routeCode: string): Promise<{
  imported: number; ops: RouteOp[]; notes: string[];
}> {
  const r = await api.post(`/drawing/${drawingId}/ops/import`,
                           { route_code: routeCode, replace: true },
                           { timeout: 60_000 });
  return r.data;
}

/**
 * 保存草稿（**整体覆盖**：界面看到什么就存什么）。
 *
 * ★ 表头**不在请求体里** —— 零件主数据是唯一权威（联动四原则之
 *   「禁止两处都能改同一字段」）。后端 `sip_drafts` 表也没有 `header_json` 列。
 *
 * ★ `is_dim` 由后端按 `nominal is not None` 现算，**客户端传了也不算数**
 *   （`_coerce_items` 只按白名单取 `item_fields.text/number/integer`）。
 *   所以这里原样把行数组发过去即可，不需要在界面侧维护第二个 `is_dim`。
 *
 * ★ 入参类型是 `Record<string, unknown>[]` 而不是 `SipItem[]`：界面上的数字字段
 *   按**字符串**持有（见 `components/sip/SipItemsTable.tsx` 文件头那个坑），
 *   提交时可能带着 `"abc"` 这种**故意不发数字**的值 —— 由后端 400 并指到第几行，
 *   界面侧不许替用户猜。写成 `SipItem[]` 会逼调用方加一层假的类型断言。
 *
 * ★ `seq` = 第几份：两份草稿互不影响（后端有专门用例钉住「改第二份不动第一份」）。
 */
export async function saveSipDraft(drawingId: number, docKind: string,
                                   items: Array<Record<string, unknown>>,
                                   remark = '', seq = 0,
                                   routeOpId = 0): Promise<SipDraftSavedResult> {
  const params: Record<string, number> = { seq };
  if (routeOpId) params.route_op_id = routeOpId;
  const r = await api.put(`/drawing/${drawingId}/sip/${docKind}`,
                          { items, remark }, { params, timeout: 120_000 });
  return r.data;
}

/** 删除**这一份**草稿 ⇒ 下次打开回落到**默认分配**（草稿只是覆盖层，不是唯一真相）。
 *
 *  ★ 第 2 份起（`seq>0`）删掉的是**整份**：后端会把后面的份号前移补洞，
 *    所以左栏不会出现「只有两份却叫制程 3」这种空洞。 */
export async function deleteSipDraft(drawingId: number, docKind: string,
                                     seq = 0,
                                     routeOpId = 0): Promise<SipDraftDeleteResult> {
  const params: Record<string, number> = { seq };
  if (routeOpId) params.route_op_id = routeOpId;
  const r = await api.delete(`/drawing/${drawingId}/sip/${docKind}`,
                             { params });
  return r.data;
}

/** v0.55：给**第 `seq` 份**改名（`title=""` ⇒ 恢复默认名「制程」/「制程 2」）。
 *
 *  ★ 该份还没有草稿行时后端会**自动建一行**（只带名字）—— 用户往往先起名再填，
 *    不该被拦；此时 `docs` 行是 `edited=false`，界面显示「未编辑明细」。
 *  ⚠️ 不能给**不存在**的份改名（想靠改名凭空造出第 5 份 ⇒ 后端 400）。 */
export async function renameSipDraft(drawingId: number, docKind: string,
                                     seq: number, title: string)
  : Promise<SipRenameResult> {
  const r = await api.patch(`/drawing/${drawingId}/sip/${docKind}`,
                            { seq, title });
  return r.data;
}

/** v0.55：**复制一份** —— 把第 `seq` 份的明细（没草稿就用它的默认初值）追加成新的一份。
 *
 *  ★ 它**写库**（新建一行草稿）⇒ 权限是 `engineer`（能改的人才配多份）。
 *  ★ 备注一并复制，**名字不复制**（新份拿默认名「制程 2」）。 */
export async function duplicateSipDraft(drawingId: number, docKind: string,
                                        seq = 0): Promise<SipDraftActionResult> {
  const r = await api.post(`/drawing/${drawingId}/sip/${docKind}/duplicate`,
                           null, { params: { seq } });
  return r.data;
}

/** v0.55：**新建空份**（0 行明细）—— 用户从零手填的那种。 */
export async function createBlankSipDraft(
  drawingId: number, docKind: string,
): Promise<SipDraftActionResult> {
  const r = await api.post(`/drawing/${drawingId}/sip/${docKind}/blank`);
  return r.data;
}

export interface SipExportResult {
  filename: string;
  /** `true` = 用的是草稿；`false` = 按工序规则现算的初值。 */
  fromDraft: boolean;
  itemCount: number;
  /** 导出的是第几份（与请求一致，读不到头时为请求值）。 */
  seq: number;
}

/**
 * 导出**第 `seq` 份** SIP 的 xlsx（**文件本体**，无 JSON 信封）。
 *
 * ★ 响应头读不到时按「未知」处理（`fromDraft=false` 会误导，所以用 `null` 语义
 *   的兜底文案交给调用方）—— 但**不许**因为读不到头就把整个下载判成失败：
 *   文件已经拿到手了，弹一句提示比让用户重下一遍强。
 * ★ 第 2 份起文件名自动带份号（后端改名，见 `sip_draft_service._deliver`）——
 *   界面**不要**自己再拼一遍后缀，否则会和后端拼出两个后缀。
 */
export async function exportSip(drawingId: number, docKind: string,
                                seq = 0,
                                style?: BalloonStyle,
                                routeOpId = 0): Promise<SipExportResult> {
  // v0.69：把画布样式一起发过去 —— -02/-03 里的气泡图是**现画的**，
  //   不带样式的话「画布关掉引线、导出的 SIP 图上还有引线」（用户第 1 条）。
  const params: Record<string, any> = { seq };
  if (style) Object.assign(params, styleQueryParams(style));
  // v0.83：工序 SIP 的导出同样要带 route_op_id —— 不带就导成「常规制程」那一份了
  if (routeOpId) params.route_op_id = routeOpId;
  const r = await api.get(`/drawing/${drawingId}/sip/${docKind}/export`,
                          { params, responseType: 'blob', timeout: 10 * 60_000 });
  let filename = `SIP_${docKind}_d${drawingId}.xlsx`;
  const cd = (r.headers['content-disposition'] as string || '');
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (m) {
    const raw = m[1].trim();
    try { filename = decodeURIComponent(raw); } catch { filename = raw; }
  }
  const fromDraft = String(r.headers['x-sip-from-draft'] ?? '') === '1';
  const nRaw = Number(r.headers['x-sip-item-count']);
  const itemCount = Number.isFinite(nRaw) ? nRaw : -1;
  const seqRaw = Number(r.headers['x-sip-seq']);
  const url = URL.createObjectURL(new Blob([r.data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
  return { filename, fromDraft, itemCount,
           seq: Number.isFinite(seqRaw) ? seqRaw : seq };
}

/**
 * 一键导出「一个产品（成品/半成品）名下所有图纸的全部 SIP」为一个 zip。
 * （后端 `GET /api/sip/export-product`，v0.90.1）
 *
 * 与 `exportSip` 同一套 blob 下载机制；`recursive` 默认 true = 一并带上
 * 该产品的下属半成品。下载完成返回后端统计头（ok/skipped/failed/elapsed）。
 */
export async function exportSipProduct(materialCode: string,
                                       recursive = true
                                       ): Promise<{ filename: string; ok: number; skipped: number; failed: number; elapsed: number }> {
  const r = await api.get('/sip/export-product', {
    params: { material_code: materialCode, recursive },
    responseType: 'blob', timeout: 30 * 60_000,
  });
  let filename = `产品SIP_${materialCode}.zip`;
  const cd = (r.headers['content-disposition'] as string || '');
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (m) {
    const raw = m[1].trim();
    try { filename = decodeURIComponent(raw); } catch { filename = raw; }
  }
  const url = URL.createObjectURL(new Blob([r.data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
  const num = (h: string) => {
    const v = Number(r.headers[h]);
    return Number.isFinite(v) ? v : 0;
  };
  return {
    filename,
    ok: num('x-sip-export-ok'),
    skipped: num('x-sip-export-skipped'),
    failed: num('x-sip-export-failed'),
    elapsed: num('x-sip-export-elapsed'),
  };
}

// ================================================================ 回收站（v0.56）
// 需求（2026-10-16 用户原话）：「需要有删除备份恢复功能，如果编辑了作业指导书
// 删除了这些数据也没有了」⇒ 四类实体（图纸 / 零件行 / BOM 关系 / 文件夹）
// 删除时都留一份快照，这里是对应的一组读写口。
//
// ⚠️ 「清空回收站」在后端是**仅管理员**（`auth_guard._ADMIN_ONLY`），
//    而且 `only_restored` 默认 **true**（只清已恢复的审计留痕）——
//    界面要显式传 false 才叫"全清"，并且必须先二次确认（见 `TrashPage`）。
export async function listTrash(opts: {
  entityType?: string; includeRestored?: boolean; limit?: number; offset?: number;
} = {}): Promise<TrashList> {
  const r = await api.get('/trash', { params: {
    entity_type: opts.entityType ?? undefined,
    include_restored: opts.includeRestored ?? false,
    limit: opts.limit ?? 200,
    offset: opts.offset ?? 0,
  } });
  return r.data;
}

/** 未恢复条数（角标轮询它 —— 只回一个数字，不拖列表数据）。 */
export async function getTrashCount(includeRestored = false): Promise<number> {
  const r = await api.get('/trash/count',
                          { params: { include_restored: includeRestored } });
  return r.data?.count ?? 0;
}

/** 单条详情：恢复后会写回哪些表、各多少行（**给用户一个交代再点恢复**）。 */
export async function getTrashDetail(id: number): Promise<TrashDetail> {
  const r = await api.get(`/trash/${id}`);
  return r.data;
}

export async function restoreTrash(id: number): Promise<TrashRestoreResult> {
  const r = await api.post(`/trash/${id}/restore`);
  return r.data;
}

/** 整批恢复（同一次用户动作产生的多条）。逐条独立提交 ⇒ 返回体分开给成败。 */
export async function restoreTrashBatch(batchId: string): Promise<TrashBatchRestoreResult> {
  const r = await api.post(`/trash/batch/${encodeURIComponent(batchId)}/restore`);
  return r.data;
}

/** **彻底**删除一条 —— 快照从此不可恢复。 */
export async function purgeTrash(id: number): Promise<TrashPurgeResult> {
  const r = await api.delete(`/trash/${id}`);
  return r.data;
}

/**
 * 清空回收站（**仅管理员**）。
 *
 * ⚠️ `onlyRestored` 的默认值是 **true**（只清已恢复的审计留痕），不是 false。
 *    后端刻意如此：这个动作最容易"把所有还能救回来的东西一起删掉"。
 *    要真全清，必须显式传 false，并且界面得二次确认写清后果。
 */
export async function clearTrash(onlyRestored = true): Promise<TrashPurgeResult> {
  const r = await api.delete('/trash', { params: { only_restored: onlyRestored } });
  return r.data;
}

// ================================================================ 选项字典（v0.61）
// 后端：`backend/app/settings_routes.py`
// ★ 读 = 登录即可（各页下拉都靠它）；写 = 仅管理员（权限矩阵里拦，不在这里判）。
export async function getOptions(): Promise<OptionsResponse> {
  const r = await api.get('/settings/options');
  return r.data;
}

export async function createOption(body: {
  kind: string; value: string;
  report_name?: string | null; precision?: number | null; note?: string;
}): Promise<OptionItem> {
  const r = await api.post('/settings/options', body);
  return r.data;
}

/** 批量新增：单条失败**整批回滚**（不制造"前 2 条进去了第 3 条没进"的中间态）。 */
export async function createOptionsBatch(kind: string,
                                         values: string[]): Promise<OptionItem[]> {
  const r = await api.post('/settings/options/batch', { kind, values });
  return r.data;
}

/** 改一项。**只提交出现的键** —— 后端用 `model_fields_set` 区分
 *  「没传（不动）」与「传了 null（清空）」，多传一个 `undefined` 是安全的。 */
export async function patchOption(id: number, body: {
  value?: string; report_name?: string | null; precision?: number | null;
  enabled?: boolean; note?: string; sort_order?: number;
}): Promise<OptionItem> {
  const r = await api.patch(`/settings/options/${id}`, body);
  return r.data;
}

/** 删除。默认拒绝"还有业务数据在用"的值（409 带 used_by），确认真删再 `force`。 */
export async function deleteOption(id: number,
                                   force = false): Promise<OptionDeleteResult> {
  const r = await api.delete(`/settings/options/${id}`, { params: { force } });
  return r.data;
}

/** 重排某一类。`ids` 必须是**本类**的 id，否则整体拒绝。 */
export async function reorderOptions(kind: string,
                                     ids: number[]): Promise<OptionItem[]> {
  const r = await api.post('/settings/options/reorder', { kind, ids });
  return r.data;
}

/** 恢复出厂：只补齐/归正**预置项**，从不删用户自己加的行。 */
export async function resetOptions(kind: string): Promise<OptionResetResult> {
  const r = await api.post('/settings/options/reset', null, { params: { kind } });
  return r.data;
}
